import { SxProps, Theme } from "@mui/material";

export const panelStyles = {
  container: {
    height: { xs: "100vh", sm: "85vh" },
    borderRadius: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    bgcolor: "#FFFFFF",
    marginRight: "auto",
    width: "100%",
  },
  header: {
    px: 2.5,
    py: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    bgcolor: "#FFFFFF",
  },
  headerTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#072531",
  },
  body: {
    px: 2.5,
    py: 2,
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  toggleButtonWrapper: {
    px: 2.5,
    mb: 1,
  },
  toggleButtonGroup: {
    width: {
      xs: "100%",
      sm: "220px",
      md: "360px",
      lg: "560px",
    },
    "& .MuiToggleButton-root": {
      flex: 1,
      borderRadius: "8px",
      padding: "6px 12px",
      fontSize: "0.85rem",
      fontWeight: 500,
      textTransform: "none",
      color: "#072531",
      backgroundColor: "#f0f0f0",
      border: "none",

      "&.Mui-selected": {
        backgroundColor: "#102434",
        color: "#fff",
        "&:hover": {
          backgroundColor: "#072531",
        },
      },

      "&:hover": {
        backgroundColor: "#e0e0e0",
      },

      "&:not(:last-of-type)": {
        marginRight: "4px",
      },
    },
  },
  toggleButton: {
    justifyContent: "center",
    border: "none",
    "&.MuiToggleButton-root": {
      border: "none",
    },
    color: "#072531",
  },
};

export const chatPanelStyles = {
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    position: "relative",
    minWidth: {
      xs: "100%", // Full width on extra small screens (phones)
      sm: "200px", // Small screens (large phones / small tablets)
      md: "300px", // Medium screens (tablets) - reduced from 400px
      lg: "400px", // Large screens (desktops) - reduced from 600px
      xl: "500px", // Extra large screens (large desktops) - reduced from 800px
    },
  },
  closeButton: {
    color: "text.secondary",
    "&:hover": {
      color: "text.primary",
      bgcolor: "action.hover",
    },
  },
  starterPromptsContainer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    gap: { xs: 0.5, sm: 1 },
    mt: { xs: 0, sm: 8 },
    px: { xs: 0.5, sm: 0 },
  },
  starterTitleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
    width: "100%",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 1, sm: 0 },
  },
  starterLogo: {
    width: { xs: "28px", sm: "32px" },
    height: { xs: "28px", sm: "32px" },
    marginRight: { xs: 0, sm: "6px" },
    marginBottom: { xs: "2px", sm: 0 },
    marginTop: { xs: "64px", sm: 0 },
  },
  starterTitle: {
    fontWeight: "bold",
    marginBottom: "4px",
    textAlign: "center",
    fontSize: { xs: "1.1rem", sm: "1.5rem" },
  },
  starterSubtitle: {
    color: "text.secondary",
    marginBottom: "10px",
    textAlign: "center",
    fontSize: { xs: "0.9rem", sm: "1rem" },
    width: "100%",
  },
  starterFAQLink: {
    display: "block",
    marginBottom: "12px",
    color: "primary.main",
    textDecoration: "none",
    textAlign: "center",
    width: "100%",
    fontSize: { xs: "0.95rem", sm: "1rem" },
    "&:hover": {
      textDecoration: "underline",
    },
  },
  starterPromptsTitle: {
    fontSize: { xs: "0.95rem", sm: "1.05rem" },
    fontWeight: 700,
    color: "text.secondary",
    mb: 1,
    textAlign: "center",
    width: "100%",
  },
  starterPromptButton: {
    borderRadius: "8px",
    backgroundColor: "#eeeeee",
    color: "text.primary",
    textTransform: "none",
    fontSize: { xs: "0.65rem", sm: "0.85rem" },
    px: { xs: 1, sm: 2.5 },
    py: { xs: 0.5, sm: 0.75 },
    boxShadow: "none",
    minWidth: "unset",
    width: "auto",
    maxWidth: { xs: "100%", sm: "none" },
    mb: { xs: 0, sm: 1 },
    "&:hover": {
      backgroundColor: "#e0e0e0",
      boxShadow: "none",
    },
    "&:focus": {
      boxShadow: "none",
    },
    "&.MuiButtonBase-root": {
      boxShadow: "none",
    },
  },
  messageList: {
    overflowY: "auto",
    px: 2.5,
    py: 2,
    display: "flex",
    flexDirection: "column",
  },
  messageItem: (role: "user" | "assistant"): SxProps<Theme> => ({
    display: "block",
    mb: 1.5,
    bgcolor: role === "user" ? "#5c948e" : "#eff6f4",
    color: "#ffffff",
    borderRadius: 1.5,
    py: 1,
    px: 1.5,
    border: "1px solid",
    borderColor: role === "user" ? "grey.100" : "transparent",
    alignSelf: role === "user" ? "flex-end" : "flex-start",
    width: "fit-content",
    maxWidth: "95%",
  }),
  messageRole: {
    mb: 0.5,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  messageContent: (role: "user" | "assistant"): SxProps<Theme> => ({
    fontSize: "0.8rem",
    lineHeight: 1.8,
    color: role === "user" ? "#ffffff" : "#072531", // <-- Conditional color
    wordWrap: "break-word",
    overflowWrap: "break-word",
    hyphens: "auto",
    "& p, & ul, & ol, & li, & pre, & code": {
      margin: 0,
      padding: 0,
      fontSize: "inherit",
      lineHeight: "inherit",
      wordWrap: "break-word",
      overflowWrap: "break-word",
    },
    "& ul, & ol": {
      paddingLeft: "2em",
    },
    "& pre": {
      background: "#f6f8fa",
      borderRadius: 2,
      padding: "0.5em",
      fontSize: "0.72rem",
      overflowX: "auto",
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
    },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      margin: "1em 0",
      fontSize: "0.9em",
    },
    "& th, & td": {
      border: "1px solid #ddd",
      padding: "8px",
      textAlign: "left",
      verticalAlign: "top",
      wordWrap: "break-word",
      overflowWrap: "break-word",
    },
    "& th": {
      backgroundColor: "#f6f8fa",
      fontWeight: 600,
    },
    "& tr:nth-of-type(even)": {
      backgroundColor: "#f8f8f8",
    },
    "& tr:hover": {
      backgroundColor: "#f5f5f5",
    },
  }),
  loadingMessage: {
    mb: 1.5,
    bgcolor: "transparent",
    borderRadius: "12px",
    px: 2,
    py: 1,
    minHeight: "unset",
    height: "32px",
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    maxWidth: "100px",
    alignSelf: "flex-start",
  },
  selectedResultsContainer: {
    px: 2.5,
    py: 2,
    borderTop: "1px solid",
    borderColor: "divider",
  },
  selectedResultsTitle: {
    mb: 1,
    color: "text.secondary",
  },
  selectedResultsList: {
    display: "flex",
    flexDirection: "row",
    gap: 1,
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      height: "2px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0, 0, 0, 0.1)",
      borderRadius: "1px",
    },
  },
  selectedResultChip: {
    flexShrink: 0,
    "& .MuiChip-deleteIcon": {
      color: "text.secondary",
      "&:hover": {
        color: "error.main",
      },
    },
  },
  inputContainer: {
    px: { xs: 1, sm: 2.5 },
    py: { xs: 1, sm: 2 },
    bgcolor: "white",
    width: "100%",
    zIndex: 2,
  },
  inputField: {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#FFFFFF",
      borderRadius: "25px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E0E0E0",
        borderRadius: "25px",
        borderWidth: "2px",
      },
    },
  },
  sendButton: {
    width: "40px",
    height: "40px",
    borderRadius: "100%",
    bgcolor: "#E0E0E0",
    color: "white",
    "&.Mui-disabled": {
      bgcolor: "#E0E0E0",
      color: "white",
    },
    transition: "all 0.2s ease-in-out",
  },
};

export const sourcesPanelStyles = {
  container: {
    height: "100%",
    borderRadius: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    bgcolor: "#FFFFFF",
    marginRight: "auto",
    width: "100%",
  },
  body: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  card: {
    bgcolor: "#FFFFFF",
    borderRadius: "16px",
    boxShadow: "none",
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    "&:hover": {
      border: (theme: Theme) => `1px solid ${theme.palette.primary.main}`,
      cursor: "pointer",
    },
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  },
  cardContent: {
    py: 0.5,
    px: 2,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  categoryPill: {
    display: "inline-block",
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.5,
    px: 1.5,
    py: 0.5,
    borderRadius: "12px",
    color: "text.secondary",
  },
  favIcon: {
    flexShrink: 0,
    color: "grey.500",
  },
  titleSection: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    mb: 1,
  },
  titleImageAndTextContainer: {
    display: "flex",
    alignItems: "flex-start",
    flexGrow: 1,
    overflow: "hidden",
    marginRight: (theme: Theme) => theme.spacing(1),
  },
  titleImage: {
    width: 32,
    height: 32,
    mr: 1.5,
    mt: 0.25,
  },
  sourceTitle: {
    fontWeight: 600,
    fontSize: {
      sm: ".6rem",
      md: ".7rem",
      lg: "1rem",
    },
    lineHeight: 1.4,
    color: "#072531",
    minWidth: 0,
    overflow: "hidden",
  },
  sourceDescription: {
    fontSize: {
      sm: ".62rem",
      md: ".68rem",
      lg: ".87rem",
    },
    lineHeight: 1.6,
    color: "text.secondary",
    mb: 2,
  },
  viewSourceLink: {
    fontWeight: 500,
    textTransform: "none",
    fontSize: "0.875rem",
    color: "#5c948e",
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "underline",
      color: "#4d7e78",
    },
  },
  noSourcesText: {
    textAlign: "center",
    color: "text.secondary",
    fontSize: "0.9rem",
    mt: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "80%",
  },
};

export const tallyFormPanelStyles = {
  container: {
    height: "100%",
    minHeight: "180px",
    display: "flex",
    color: "primary",
    width: "100%",
    flexDirection: "column",
    bgcolor: "background.paper",
    borderRadius: 1,
    overflow: "auto",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  formContainer: {
    width: "100%",
    display: "flex",
    overflow: "auto",
    flexGrow: 1,
  },
} as const;
